import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as widgetDataService from '../common/services/next-gen/WidgetDataService';

/**
 * Invalidates all exam-related widget queries (data + counts).
 * Call after any exam modification (mark performed, save, delete, etc.)
 * so every widget on the dashboard refreshes.
 */
export function invalidateAllWidgetQueries(queryClient) {
    queryClient.invalidateQueries({ queryKey: ['widgetData'] });
    queryClient.invalidateQueries({ queryKey: ['widgetCount'] });
}

/**
 * Invalidate only the named widgets' queries. Prefer this after single-widget actions
 * (print, dismiss): broadcasting to every widget refetches a dozen expensive counts at
 * once, which is exactly the concurrent burst that made the dashboard time out. The
 * 60s staleTime on counts keeps the other badges honest without a stampede.
 */
export function invalidateWidgetQueries(queryClient, widgetKeys) {
    for (const key of widgetKeys) {
        queryClient.invalidateQueries({ queryKey: ['widgetData', key] });
        queryClient.invalidateQueries({ queryKey: ['widgetCount', key] });
    }
}

/**
 * Hook that returns a stable callback to invalidate all widget queries.
 */
export function useInvalidateAllWidgets() {
    const queryClient = useQueryClient();
    return useCallback(() => invalidateAllWidgetQueries(queryClient), [queryClient]);
}

/**
 * Hook to fetch count for a specific widget
 * @param {string} widgetKey - The widget key identifier
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useWidgetCount(widgetKey, enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', widgetKey, selectedFacilities],
        queryFn: () => widgetDataService.getWidgetCount(widgetKey, selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch full data for a specific widget
 * @template T
 * @param {string} widgetKey - The widget key identifier
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useWidgetData(widgetKey, enabled = false, selectedFacilities = []) {
    return useQuery({
        queryKey: ['widgetData', widgetKey, selectedFacilities],
        queryFn: () => widgetDataService.getWidgetData(widgetKey, selectedFacilities),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0, // Always refetch when query becomes active to keep data current
        cacheTime: 10 * 60 * 1000 // 10 minutes - keeps cached data for instant display while refetching
    });
}

/**
 * Hook to fetch counts for multiple widgets
 * @param {string[]} widgetKeys - Array of widget key identifiers
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useWidgetCounts(widgetKeys, enabled = true) {
    return useQuery({
        queryKey: ['widgetCounts', widgetKeys],
        queryFn: () => widgetDataService.getWidgetCounts(widgetKeys),
        enabled: enabled && widgetKeys.length > 0,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch exams without results
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useExamsWithoutResults(enabled = false, selectedFacilities = []) {
    return useWidgetData('exams-without-results', enabled, selectedFacilities);
}

/**
 * Hook to fetch count of exams without results
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useExamsWithoutResultsCount(enabled = true, selectedFacilities = []) {
    return useWidgetCount('exams-without-results', enabled, selectedFacilities);
}

/**
 * Hook to fetch exams without results filtered by date range
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useExamsWithoutResultsFiltered(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'exams-without-results-filtered', request],
        queryFn: () => widgetDataService.getExamsWithoutResultsFiltered(request),
        enabled: enabled && request !== null,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch count of unperformed exams
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/unperformedExamsTypes').UnperformedExamsCountResponse>}
 */
export function useUnperformedExamsCount(enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'unperformed-exams', selectedFacilities],
        queryFn: () => widgetDataService.getUnperformedExamsCount(selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch unperformed exams data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/unperformedExamsTypes').UnperformedExamsDataResponse>}
 */
export function useUnperformedExamsData(page = 1, pageSize = 50, enabled = false, selectedFacilities = []) {
    return useQuery({
        queryKey: ['widgetData', 'unperformed-exams', page, pageSize, selectedFacilities],
        queryFn: () => widgetDataService.getUnperformedExamsData(page, pageSize, selectedFacilities),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to delete an exam
 * @returns {import('@tanstack/react-query').UseMutationResult<{message: string}, Error, {examId: string, reasonForDeletion: string, deleteBothGroupedExams: boolean}>}
 */
export function useDeleteExam() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: ({ examId, reasonForDeletion, deleteBothGroupedExams }) => 
            widgetDataService.deleteExam(examId, reasonForDeletion, deleteBothGroupedExams),
        onSuccess: () => {
            // Invalidate all widget queries since exam deletion can affect multiple widgets
            invalidateAllWidgetQueries(queryClient);
        }
    });
}

/**
 * Hook to fetch count of follow-up tracking records
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').FollowUpTrackingCountResponse>}
 */
export function useFollowUpTrackingCount(enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'follow-up-tracking', selectedFacilities],
        queryFn: () => widgetDataService.getFollowUpTrackingCount(selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch follow-up tracking data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').FollowUpTrackingDataResponse>}
 */
export function useFollowUpTrackingData(page = 1, pageSize = 50, enabled = false, search = '', selectedFacilities = []) {
    return useQuery({
        queryKey: ['widgetData', 'follow-up-tracking', page, pageSize, search, selectedFacilities],
        queryFn: () => widgetDataService.getFollowUpTrackingData(page, pageSize, search, selectedFacilities),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch count of exams without results filtered by date range
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useExamsWithoutResultsFilteredCount(request, enabled = false) {
    // Wait for the SSE count stream before firing this count - racing it adds to the dashboard's SQL burst
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'exams-without-results-filtered', request],
        queryFn: () => widgetDataService.getExamsWithoutResultsFilteredCount(request),
        enabled: enabled && request !== null && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch count of follow-up tracking filtered records
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone, selectedFacilities, selectedBirads)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useFollowUpTrackingFilteredCount(request, enabled = false) {
    // Wait for the SSE count stream before firing this count - racing it adds to the dashboard's SQL burst
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'follow-up-tracking-filtered', request],
        queryFn: () => widgetDataService.getFollowUpTrackingFilteredCount(request),
        enabled: enabled && request !== null && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch follow-up tracking records filtered by date range, facilities, and BIRADS
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone, selectedFacilities, selectedBirads)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useFollowUpTrackingFiltered(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'follow-up-tracking-filtered', request],
        queryFn: () => widgetDataService.getFollowUpTrackingFiltered(request),
        enabled: enabled && request !== null,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch count of incomplete exams
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').IncompleteExamsCountResponse>}
 */
export function useIncompleteExamsCount(enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'exams-missing-info', selectedFacilities],
        queryFn: () => widgetDataService.getIncompleteExamsCount(selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch incomplete exams data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').IncompleteExamsDataResponse>}
 */
export function useIncompleteExamsData(page = 1, pageSize = 50, enabled = false, selectedFacilities = []) {
    return useQuery({
        queryKey: ['widgetData', 'exams-missing-info', page, pageSize, selectedFacilities],
        queryFn: () => widgetDataService.getIncompleteExamsData(page, pageSize, selectedFacilities),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch count of result letters not printed
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').ResultLetterNotPrintedCountResponse>}
 */
export function useResultLetterNotPrintedCount(enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'result-letters-not-printed', selectedFacilities],
        queryFn: () => widgetDataService.getResultLetterNotPrintedCount(selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch result letters not printed data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').ResultLetterNotPrintedDataResponse>}
 */
export function useResultLetterNotPrintedData(page = 1, pageSize = 50, enabled = false, examTypes = [], selectedFacilities = []) {
    return useQuery({
        queryKey: ['widgetData', 'result-letters-not-printed', page, pageSize, examTypes, selectedFacilities],
        queryFn: () => widgetDataService.getResultLetterNotPrintedData(page, pageSize, examTypes, selectedFacilities),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch count of missing same-day provider visits
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').MissingSameDayProviderVisitsCountResponse>}
 */
export function useMissingSameDayProviderVisitsCount(enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'missing-same-day-provider-visits', selectedFacilities],
        queryFn: () => widgetDataService.getMissingSameDayProviderVisitsCount(selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch missing same-day provider visits data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').MissingSameDayProviderVisitsDataResponse>}
 */
export function useMissingSameDayProviderVisitsData(page = 1, pageSize = 50, enabled = false, selectedFacilities = []) {
    return useQuery({
        queryKey: ['widgetData', 'missing-same-day-provider-visits', page, pageSize, selectedFacilities],
        queryFn: () => widgetDataService.getMissingSameDayProviderVisitsData(page, pageSize, selectedFacilities),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch tech summary data
 * @param {Object} request - Tech summary request parameters
 * @param {string} request.startDate - Start date (ISO string)
 * @param {string} request.endDate - End date (ISO string)
 * @param {string[]} request.selectedFacilities - Array of facility IDs
 * @param {string[]} request.selectedTechs - Array of technologist user IDs
 * @param {string[]} request.selectedExamTypes - Array of exam type IDs
 * @param {boolean} request.showDetails - Whether to include exam details
 * @param {string} request.ianaTimezone - Client timezone
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<{techName: string, userId: string, totalExams: number, details: Array}[]>}
 */
export function useTechSummaryData(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'tech-summary', request],
        queryFn: () => widgetDataService.getTechSummaryData(request),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000
    });
}

const STREAM_DONE_KEY = ['widgetStream', 'done'];

/**
 * Streams all widget counts via SSE and writes results into the TanStack Query
 * cache. Sets a sentinel cache key when complete (or on failure) so widgets
 * know when to enable their individual fallback queries.
 */
export function useWidgetCountsStream(selectedFacilities = [], widgetKeys = []) {
    const queryClient = useQueryClient();
    const facilitiesKey = selectedFacilities.join(',');
    const widgetKeysKey = widgetKeys.join(',');

    useEffect(() => {
        let cleanup = () => {};
        let startTimer = null;
        let unmounted = false;

        // Re-arm the "wait for stream" gate for this run. Without this, STREAM_DONE_KEY
        // stays true from a previous run (e.g. the initial load) forever, so widgets never
        // wait for a re-opened stream after the facility filter changes - they'd race it
        // with their own individual fetch instead of relying on this stream's cache writes.
        queryClient.setQueryData(STREAM_DONE_KEY, false);

        function signalDone() {
            queryClient.setQueryData(STREAM_DONE_KEY, true);
        }

        function start() {
            if (unmounted) return;
            // Safety valve: signal done after 10s so widgets can fall back to individual queries
            const timeoutId = setTimeout(() => { if (!unmounted) signalDone(); }, 10000);
            cleanup = widgetDataService.streamWidgetCounts(
                (widgetKey, count) => {
                    // Must match each widget's own count query key exactly (['widgetCount', widgetKey, selectedFacilities])
                    // or this pre-populated cache entry is never read and every widget re-fetches its own count anyway.
                    queryClient.setQueryData(
                        ['widgetCount', widgetKey, selectedFacilities],
                        widgetKey === 'exams-without-results'
                            ? count
                            : { widgetKey, mode: 'count', count, data: null }
                    );
                },
                (widgetKey, error) => {
                    console.warn(`Widget stream reported an error for "${widgetKey}":`, error);
                },
                () => { clearTimeout(timeoutId); signalDone(); },
                () => {
                    if (unmounted) return;
                    clearTimeout(timeoutId);
                    signalDone();
                },
                selectedFacilities,
                widgetKeys
            );
        }

        // Defer to the next tick so App.js's useEffect (which sets Globals.nextGenToken)
        // has already run before we attempt the stream connection.
        startTimer = setTimeout(start, 0);

        return () => {
            unmounted = true;
            cleanup();
            clearTimeout(startTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryClient, facilitiesKey, widgetKeysKey]);
}

/**
 * Returns true once the widget count stream has completed (or exhausted retries).
 * Widgets use this to gate their individual count queries � if the stream already
 * populated the cache, TanStack Query will skip the network request entirely.
 */
export function useStreamComplete() {
    const { data } = useQuery({
        queryKey: STREAM_DONE_KEY,
        enabled: false,
        staleTime: Infinity,
    });
    return !!data;
}

/**
 * Hook to fetch count of exam perform failures
 * @param {boolean} [enabled=true]
 */
export function useExamPerformFailuresCount(enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'exam-perform-failures', selectedFacilities],
        queryFn: () => widgetDataService.getExamPerformFailuresCount(selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch exam perform failures data with server-side pagination
 * @param {number} page
 * @param {number} pageSize
 * @param {boolean} [enabled=false]
 */
export function useExamPerformFailuresData(page = 1, pageSize = 50, enabled = false, selectedFacilities = []) {
    return useQuery({
        queryKey: ['widgetData', 'exam-perform-failures', page, pageSize, selectedFacilities],
        queryFn: () => widgetDataService.getExamPerformFailuresData(page, pageSize, selectedFacilities),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch exam perform failures filtered by date range and facilities
 * @param {Object|null} request - includes startDate, endDate, ianaTimezone, selectedFacilities, page, pageSize
 * @param {boolean} [enabled=false]
 */
export function useExamPerformFailuresFiltered(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'exam-perform-failures-filtered', request],
        queryFn: () => widgetDataService.getExamPerformFailuresFiltered(request),
        enabled: enabled && request !== null,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch count of exam perform failures filtered
 * @param {Object|null} request
 * @param {boolean} [enabled=false]
 */
export function useExamPerformFailuresFilteredCount(request, enabled = false) {
    // Wait for the SSE count stream before firing this count - racing it adds to the dashboard's SQL burst
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'exam-perform-failures-filtered', request],
        queryFn: () => widgetDataService.getExamPerformFailuresFilteredCount(request),
        enabled: enabled && request !== null && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Mutation hook to reconcile an exam perform failure
 */
export function useReconcileExamPerformFailure() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => widgetDataService.reconcileExamPerformFailure(data),
        onSuccess: () => {
            invalidateAllWidgetQueries(queryClient);
        }
    });
}

/**
 * Mutation hook to dismiss an exam perform failure
 */
export function useDismissExamPerformFailure() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => widgetDataService.dismissExamPerformFailure(id),
        onSuccess: () => {
            invalidateAllWidgetQueries(queryClient);
        }
    });
}

/**
 * Hook to fetch count of result entry failures
 * @param {boolean} [enabled=true]
 */
export function useResultEntryFailuresCount(enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'result-entry-failures', selectedFacilities],
        queryFn: () => widgetDataService.getResultEntryFailuresCount(selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch result entry failures data with server-side pagination
 * @param {number} page
 * @param {number} pageSize
 * @param {Object} filters
 * @param {boolean} [enabled=false]
 */
export function useResultEntryFailuresData(page = 1, pageSize = 50, filters = {}, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'result-entry-failures', page, pageSize, filters],
        queryFn: () => widgetDataService.getResultEntryFailuresData(page, pageSize, filters),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Mutation hook to reconcile a result entry failure
 */
export function useReconcileResultEntryFailure() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => widgetDataService.reconcileResultEntryFailure(data),
        onSuccess: () => {
            invalidateAllWidgetQueries(queryClient);
        }
    });
}

/**
 * Mutation hook to dismiss a result entry failure
 */
export function useDismissResultEntryFailure() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => widgetDataService.dismissResultEntryFailure(id),
        onSuccess: () => {
            invalidateAllWidgetQueries(queryClient);
        }
    });
}

/**
 * Hook to fetch count of print house monitoring results requiring action
 * @param {boolean} [enabled=true]
 */
export function usePrintHouseMonitoringCount(enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'print-house-monitoring', selectedFacilities],
        queryFn: () => widgetDataService.getPrintHouseMonitoringCount(selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch print house monitoring data with server-side pagination
 * @param {string} [dataset='requiring-action'] - 'requiring-action' or 'pending-print'
 * @param {number} [page=1]
 * @param {number} [pageSize=50]
 * @param {boolean} [enabled=false]
 */
export function usePrintHouseMonitoringData(dataset = 'requiring-action', page = 1, pageSize = 50, enabled = false, examTypes = [], selectedFacilities = []) {
    return useQuery({
        queryKey: ['widgetData', 'print-house-monitoring', dataset, page, pageSize, examTypes, selectedFacilities],
        queryFn: () => widgetDataService.getPrintHouseMonitoringData(dataset, page, pageSize, examTypes, selectedFacilities),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000
        // Deliberately no keepPreviousData: dataset ('requiring-action' vs 'pending-print') is a distinct
        // toggle, not just a page increment - keeping the previous dataset's rows visible while the new
        // one loads caused rows to briefly appear under the wrong view when the checkbox was toggled.
    });
}

/**
 * Optimistically drop a result from the print-house widget's cached pages and decrement
 * its badge count, returning the previous entries so a caller can roll back.
 *
 * Exported because two different actions remove a row: dismissing it (below) and printing
 * its letter in-app (the widget's print handler) - an in-app print writes a PrintHistory
 * row, which is exactly what the widget query excludes on. Both need the row and the badge
 * to update immediately rather than after a refetch: the collapsed badge's own query is
 * disabled while the widget is expanded, so invalidation alone leaves the count showing a
 * stale number until the user collapses AND a refetch completes.
 */
export function removePrintHouseRowFromCache(queryClient, patientResultId) {
    const previousData = queryClient.getQueriesData({ queryKey: ['widgetData', 'print-house-monitoring'] });
    const previousCounts = queryClient.getQueriesData({ queryKey: ['widgetCount', 'print-house-monitoring'] });

    queryClient.setQueriesData({ queryKey: ['widgetData', 'print-house-monitoring'] }, (old) => {
        if (!old?.data) return old;
        const remaining = old.data.filter(row => row.patientResultId !== patientResultId);
        if (remaining.length === old.data.length) return old;
        return { ...old, data: remaining, totalCount: Math.max(0, old.totalCount - 1) };
    });

    queryClient.setQueriesData({ queryKey: ['widgetCount', 'print-house-monitoring'] }, (old) =>
        old ? { ...old, count: Math.max(0, old.count - 1) } : old
    );

    return { previousData, previousCounts };
}

export function useDismissPrintHouseMonitoring() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (patientResultId) => widgetDataService.dismissPrintHouseMonitoring(patientResultId),
        onMutate: async (patientResultId) => {
            await queryClient.cancelQueries({ queryKey: ['widgetData', 'print-house-monitoring'] });
            await queryClient.cancelQueries({ queryKey: ['widgetCount', 'print-house-monitoring'] });
            return removePrintHouseRowFromCache(queryClient, patientResultId);
        },
        onError: (_err, _patientResultId, context) => {
            // Put the row back if the dismiss did not actually stick.
            context?.previousData?.forEach(([key, value]) => queryClient.setQueryData(key, value));
            context?.previousCounts?.forEach(([key, value]) => queryClient.setQueryData(key, value));
        },
        // A dismiss here can also change result-letters-not-printed (same underlying result),
        // but nothing else - scoped invalidation, not a broadcast, so one click does not
        // stampede every widget's queries at once.
        onSettled: () => invalidateWidgetQueries(queryClient, ['print-house-monitoring', 'result-letters-not-printed'])
    });
}

/**
 * Hook to fetch count of unprinted recall letters
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useUnprintedRecallLettersCount(enabled = true, selectedFacilities = []) {
    // Wait for the SSE count stream before firing the fallback fetch - racing it doubles dashboard SQL load
    const streamComplete = useStreamComplete();
    return useQuery({
        queryKey: ['widgetCount', 'unprinted-recall-letters', selectedFacilities],
        queryFn: () => widgetDataService.getUnprintedRecallLettersCount(selectedFacilities),
        enabled: enabled && streamComplete,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 60 * 1000, // short, so returning to the dashboard re-fetches without re-querying on every mount
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch unprinted recall letters data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useUnprintedRecallLettersData(page = 1, pageSize = 50, enabled = false, selectedFacilities = [], recallTypes = [], printStatuses = []) {
    return useQuery({
        queryKey: ['widgetData', 'unprinted-recall-letters', page, pageSize, selectedFacilities, recallTypes, printStatuses],
        queryFn: () => widgetDataService.getUnprintedRecallLettersData(page, pageSize, selectedFacilities, recallTypes, printStatuses),
        enabled,
        retry: 1, // default 3 retries multiplied the dashboard's concurrent query burst
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

export function useDismissUnprintedRecallLetter() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (patientRecallId) => widgetDataService.dismissUnprintedRecallLetter(patientRecallId),
        // Drop the row from the cache straight away rather than waiting on a refetch.
        // Invalidating alone was not enough: a dismiss invalidates every widget query,
        // and on a large dataset that refetch can be slow, aborted or time out - leaving
        // the dismissed row on screen until a hard refresh even though the write landed.
        onMutate: async (patientRecallId) => {
            await queryClient.cancelQueries({ queryKey: ['widgetData', 'unprinted-recall-letters'] });
            await queryClient.cancelQueries({ queryKey: ['widgetCount', 'unprinted-recall-letters'] });

            const previousData = queryClient.getQueriesData({ queryKey: ['widgetData', 'unprinted-recall-letters'] });
            const previousCounts = queryClient.getQueriesData({ queryKey: ['widgetCount', 'unprinted-recall-letters'] });

            queryClient.setQueriesData({ queryKey: ['widgetData', 'unprinted-recall-letters'] }, (old) => {
                if (!old?.data) return old;
                const remaining = old.data.filter(row => row.patientRecallId !== patientRecallId);
                if (remaining.length === old.data.length) return old;
                return { ...old, data: remaining, totalCount: Math.max(0, old.totalCount - 1) };
            });

            queryClient.setQueriesData({ queryKey: ['widgetCount', 'unprinted-recall-letters'] }, (old) =>
                old ? { ...old, count: Math.max(0, old.count - 1) } : old
            );

            return { previousData, previousCounts };
        },
        onError: (_err, _patientRecallId, context) => {
            // Put the row back if the dismiss did not actually stick.
            context?.previousData?.forEach(([key, value]) => queryClient.setQueryData(key, value));
            context?.previousCounts?.forEach(([key, value]) => queryClient.setQueryData(key, value));
        },
        // Reconcile with the server either way.
        // Only this widget: a recall dismiss cannot change any other widget, and the old
        // broadcast refetched every count+table at once, recreating the timeout herd.
        onSettled: () => invalidateWidgetQueries(queryClient, ['unprinted-recall-letters'])
    });
}







